"use client";

import { useSyncExternalStore } from "react";

// WebGL scenes cost seconds of main-thread time on phones and GPU-less machines (shader
// compilation, environment maps, software rasterisation), which would land in the middle
// of page load. They wait for the reader's first sign of life instead: any pointer, key,
// touch, wheel or scroll. Until then each scene shows its lightweight poster.
const EVENTS = ["pointerdown", "pointermove", "keydown", "touchstart", "wheel", "scroll"] as const;
let engaged = false;
const listeners = new Set<() => void>();

function engage() {
  if (engaged) return;
  engaged = true;
  for (const name of EVENTS) window.removeEventListener(name, engage, true);
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  if (!engaged && listeners.size === 0) for (const name of EVENTS) window.addEventListener(name, engage, { capture: true, passive: true });
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** False until the reader first interacts with the page, then true for the rest of the visit. */
export function useEngaged() {
  return useSyncExternalStore(subscribe, () => engaged, () => false);
}

type DeviceHints = { saveData?: boolean; deviceMemory?: number; hardwareConcurrency?: number };

/**
 * Whether a device can afford to start a WebGL scene without being asked: no data saver,
 * at least 4 GB of memory and 4 cores where the browser reports them (Safari and Firefox
 * report neither, and get the benefit of the doubt).
 */
export function canStartUnprompted(hints: DeviceHints): boolean {
  if (hints.saveData) return false;
  if (hints.deviceMemory !== undefined && hints.deviceMemory < 4) return false;
  if (hints.hardwareConcurrency !== undefined && hints.hardwareConcurrency < 4) return false;
  return true;
}

let idleScheduled = false;

function scheduleIdle() {
  if (idleScheduled || typeof window === "undefined") return;
  idleScheduled = true;
  const nav = navigator as Navigator & { connection?: { saveData?: boolean }; deviceMemory?: number };
  if (!canStartUnprompted({ saveData: nav.connection?.saveData, deviceMemory: nav.deviceMemory, hardwareConcurrency: nav.hardwareConcurrency })) return;
  const start = () => {
    if ("requestIdleCallback" in window) window.requestIdleCallback(engage, { timeout: 1200 });
    else setTimeout(engage, 300);
  };
  if (document.readyState === "complete") start();
  else window.addEventListener("load", start, { once: true });
}

/**
 * Like useEngaged, but capable devices also start once the page has loaded and the main
 * thread is idle, so a hero scene appears without waiting for the reader to move.
 */
export function useEngagedOrIdle() {
  return useSyncExternalStore((listener) => { scheduleIdle(); return subscribe(listener); }, () => engaged, () => false);
}
